import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { can, esAnalista, useAuth } from "../../lib/auth";
import { money } from "../../lib/casePath";
import { Btn, Card } from "../kit/Kit";
import { useAsignables, useRefs } from "./Traza";
import { DealCovenants } from "./DealCovenants";
import { VerExpDrive } from "../lm/VerExpDrive";
import { CamposPanel } from "../campos/CamposPanel";

const sel = "mt-1 w-full rounded-lg border border-[#E2DDD5] bg-[#FAF8F5] px-3 py-2 text-sm";

const F = ({ v, setV, k, l, opts, none }) => (
  <label className="block text-[11px] font-semibold uppercase tracking-widest text-[#8C867A]">{l}
    <select value={v[k]} onChange={(e) => setV({ ...v, [k]: e.target.value })} className={sel} data-testid={`cf-${k}`}><option value="">{none}</option>{opts.map((o) => <option key={o.id} value={o.id}>{o.name || o.nombre}</option>)}</select></label>
);

const Asignar = ({ c, onSaved }) => {
  const { comerciales, analistas } = useAsignables();
  const [refs] = useRefs();
  const [v, setV] = useState({ comercial_id: c.comercial_id || "", analista_id: c.owner_id || "", referenciador_id: c.referenciador_id || "" });
  const save = async () => {
    try { onSaved((await http.put(`/pipeline/cartera/${c.id}/asignar`, { comercial_id: v.comercial_id || null, analista_id: v.analista_id || null, referenciador_id: v.referenciador_id || null })).data); toast.success("Asignación guardada (también en Completar datos)"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="Responsables" testid="cf-asignar">
      <div className="space-y-3"><F v={v} setV={setV} k="comercial_id" l="Ejecutivo comercial" opts={comerciales.filter((u) => u.role === "comercial")} none="Trafalgar Direct" /><F v={v} setV={setV} k="analista_id" l="Analista" opts={analistas} none="Sin analista" /><F v={v} setV={setV} k="referenciador_id" l="Referenciador" opts={refs} none="Sin referenciador" /></div>
      <Btn className="mt-4 w-full" onClick={save} data-testid="cf-guardar">Guardar asignación</Btn>
    </Card>
  );
};

export const CarteraFicha = ({ id }) => {
  const { user } = useAuth();
  const [c, setC] = useState(null);
  useEffect(() => { http.get(`/pipeline/cartera/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e))); }, [id]);
  if (!c) return <main className="p-10 text-sm text-[#8C867A]">Cargando…</main>;
  const rows = [["Acreditado", c.razon], ["RFC", c.rfc || "Sin dato"], ["Tipo", c.tipo], ["Producto", c.producto], ["Plazo", c.plazo || "Sin dato"], ["Tasa", c.tasa != null ? `${(c.tasa * 100).toFixed(2)}%` : "Sin dato"],
    ["Estado", c.estado || "Sin dato"], ["Monto dispuesto", money(c.recursos)], ["Fecha de disposición", c.disposicion.fecha], ["Saldo insoluto", money(c.saldo)], ["Días de atraso", c.dpd], ["Estatus", c.lm_status], ["Score", "Sin dato"],
    ["Ejecutivo comercial", c.comercial_name], ["Analista", c.owner_name || "Sin asignar"], ["Referenciador", c.referenciador_name || "—"]];
  return (
    <main className="mx-auto max-w-5xl space-y-6 px-4 py-8 sm:px-6" data-testid="cartera-ficha">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div><span className="rounded-full border border-[#13315C]/20 bg-[#13315C]/5 px-2.5 py-1 text-xs font-semibold text-[#13315C]" data-testid="cf-origen">Cartera importada (SOFIPO)</span>
          <h1 className="mt-3 font-serif-display text-3xl font-bold sm:text-4xl" data-testid="cf-nombre">{c.razon}</h1><p className="mt-1 font-mono text-sm text-[#8C867A]">{c.lm_loan_id} · Dispuesto (etapa final)</p></div>
        <Link to={`/cartera/creditos?loan=${encodeURIComponent(c.lm_loan_id)}&dataset=sofipo`} className="inline-flex items-center gap-2 rounded-full bg-[#E8643C] px-5 py-3 text-sm font-bold text-white hover:bg-[#d4552f]" data-testid="cf-ver-credito">Ver crédito en Cartera <ArrowRight size={16} /></Link>
      </div>
      <div className="grid gap-6 md:grid-cols-[2fr_1fr]">
        <Card title="Resumen del crédito" subtitle="Datos leídos de Loan Management (fuente de verdad). Sin wizard, dictamen ni memo." testid="cf-resumen">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">{rows.map(([l, v]) => <div key={l}><dt className="text-[11px] uppercase tracking-widest text-[#8C867A]">{l}</dt><dd className="font-semibold" data-testid={`cf-${l.toLowerCase().replace(/\W+/g, "-")}`}>{v}</dd></div>)}</dl>
        </Card>
        {can(user, "deals.editar") && <Asignar c={c} onSaved={setC} />}
      </div>
      {esAnalista(user) && <div data-testid="cf-expediente"><VerExpDrive lid={c.lm_loan_id} /></div>}
      <div data-testid="cf-covenants"><DealCovenants id={id} exp={{ id, stage: "dispuesto", nombre: c.razon, owner_id: c.owner_id, disposicion: c.disposicion }} /></div>
          <CamposPanel entidad="credito" refId={c.lm_loan_id} />
    </main>
  );
};
